/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { type MessageDescriptor, useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { jsx } from '@atlaskit/css';
import Form from '@atlaskit/form';
import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';

import { type ImpactedWorkResolutionStatus } from '../fields/impacted-work/useImpactedWorkResolution';
import { MAX_ASK_NAME_LENGTH } from '../fields/name';
import { FocusAskFormFields } from '../form-fields/FocusAskFormFields';
import { emptyFocusAskTargetDateValue } from '../format-focus-ask-target-date';
import messages from '../messages';
import { type FocusAskFormData } from '../types';

const DEFAULT_FORM_DATA: FocusAskFormData = {
	name: '',
	description: '',
	justification: '',
	targetDate: emptyFocusAskTargetDateValue(),
	commentData: '',
	links: [],
	submitter: null,
	owner: null,
	submittingTeam: null,
	receivingTeam: null,
	priority: 'MEDIUM',
	impactedWork: null,
};

export type FocusAskCreateModalProps = {
	data: FocusAskFormData | null;
	cloudId: string;
	orgId: string;
	onClose: () => void;
	onSubmit: (formData: FocusAskFormData) => void;
	title?: MessageDescriptor;
	submitLabel?: MessageDescriptor;
};

export const FocusAskCreateModal = ({
	data,
	cloudId,
	orgId,
	onClose,
	onSubmit,
	title = messages.modalTitle,
	submitLabel = messages.createButton,
}: FocusAskCreateModalProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const defaultComment = formatMessage(messages.commentPlaceholder);
	const [formState, setFormState] = useState<FocusAskFormData>(() => ({
		...(data ?? DEFAULT_FORM_DATA),
		commentData: data?.commentData?.trim() ? data.commentData : defaultComment,
	}));
	const [impactedWorkStatus, setImpactedWorkStatus] = useState<ImpactedWorkResolutionStatus>(
		data?.impactedWork ? 'resolving' : 'idle',
	);
	const previousDataRef = useRef(data);

	useLayoutEffect(() => {
		if (data && previousDataRef.current !== data) {
			setFormState({
				...data,
				commentData: data.commentData?.trim() ? data.commentData : defaultComment,
			});
		}

		previousDataRef.current = data;
	}, [data, defaultComment]);

	const updateField = useCallback(
		<K extends keyof FocusAskFormData>(field: K, value: FocusAskFormData[K]) => {
			setFormState((prev) => ({ ...prev, [field]: value }));
		},
		[],
	);

	const isImpactedWorkBlockingSubmit =
		impactedWorkStatus !== 'idle' && impactedWorkStatus !== 'resolved';

	const isSubmitDisabled = useMemo(
		() =>
			!formState.name.trim() ||
			!formState.submitter?.id?.trim() ||
			formState.name.length > MAX_ASK_NAME_LENGTH ||
			isImpactedWorkBlockingSubmit,
		[formState.name, formState.submitter, isImpactedWorkBlockingSubmit],
	);

	const handleSubmit = useCallback(() => {
		if (isSubmitDisabled) {
			return;
		}
		onSubmit({ ...formState, name: formState.name.trim() });
	}, [formState, isSubmitDisabled, onSubmit]);

	return (
		<ModalTransition>
			{data && (
				<Modal onClose={onClose} width="medium" shouldScrollInViewport>
					<ModalHeader hasCloseButton>
						<ModalTitle>{formatMessage(title)}</ModalTitle>
					</ModalHeader>
					<ModalBody>
						<Form onSubmit={handleSubmit}>
							{() => (
								<form
									onSubmit={(e) => {
										e.preventDefault();
										handleSubmit();
									}}
								>
									<FocusAskFormFields
										formData={formState}
										onChange={updateField}
										cloudId={cloudId}
										orgId={orgId}
										fieldIdPrefix="focus-ask-modal"
										onImpactedWorkResolutionStatusChange={setImpactedWorkStatus}
									/>
								</form>
							)}
						</Form>
					</ModalBody>
					<ModalFooter>
						<Button appearance="subtle" onClick={onClose}>
							{formatMessage(messages.cancelButton)}
						</Button>
						<Button appearance="primary" onClick={handleSubmit} isDisabled={isSubmitDisabled}>
							{formatMessage(submitLabel)}
						</Button>
					</ModalFooter>
				</Modal>
			)}
		</ModalTransition>
	);
};
